import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../hooks/useAuth';

/**
 * Custom hook to manage punchout session loading and initialization.
 * Handles both cXML-initiated sessions (already created server-side) and
 * JSON/simulator sessions (initialized client-side).
 *
 * @param {Object} options
 * @param {string} options.punchoutType - Default punchout type ('cxml' or 'oci')
 * @param {string} options.returnUrl - Fallback return URL from props
 * @param {string} options.buyerCookie - Fallback session ID from props
 * @param {string} options.accountID - Account ID for session initialization
 * @returns {Object} Session state and controls
 */
export function usePunchoutSession({
    punchoutType = 'cxml',
    returnUrl = null,
    buyerCookie = null,
    accountID = null
} = {}) {
    const [punchoutSession, setPunchoutSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Derive effective punchout type from session (if available) or prop fallback
    const effectivePunchoutType = punchoutSession?.punchoutType || punchoutType;

    const loadSession = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            // Extract punchout parameters from URL or props
            const urlParams = new URLSearchParams(window.location.search);
            const sessionId = urlParams.get('sessionId') || buyerCookie;
            const returnUrlParam = urlParams.get('returnUrl') || returnUrl;

            if (!sessionId) {
                throw new Error('Punchout session ID is required');
            }

            // First, try to load existing session (created by cXML request)
            try {
                const existingSessionResponse = await apiFetch(
                    `/app/punchout/session?sessionId=${encodeURIComponent(sessionId)}`
                );
                
                if (existingSessionResponse.ok) {
                    const responseData = await existingSessionResponse.json();
                    const sessionData = responseData.session || responseData;

                    setPunchoutSession({
                        ...sessionData,
                        sessionId: sessionData.sessionId || sessionId
                    });
                    
                    return sessionData;
                }
            } catch (loadErr) {
                // No existing session found, will try to initialize
            }

            // Only initialize if session doesn't exist (JSON/simulator flow)
            if (!returnUrlParam) {
                throw new Error(
                    'Punchout session not found. Please start a new punchout session from your procurement system.'
                );
            }

            // Initialize punchout session (only for JSON/simulator mode)
            const response = await apiFetch('/app/punchout/initialize', {
                method: 'POST',
                body: JSON.stringify({
                    sessionId,
                    returnUrl: returnUrlParam,
                    punchoutType,
                    accountID
                })
            });

            const sessionData = await response.json();
            setPunchoutSession(sessionData);
            
            return sessionData;

        } catch (err) {
            setError(err.message);
            return null;
        } finally {
            setLoading(false);
        }
    }, [buyerCookie, returnUrl, punchoutType, accountID]);

    // Load session on mount
    useEffect(() => {
        loadSession();
    }, [loadSession]);

    return {
        punchoutSession,
        effectivePunchoutType,
        loading,
        error,
        reloadSession: loadSession
    };
}
